import type { PropsWithChildren, ReactNode } from 'react';
import { createContext, useCallback, useContext, useState } from 'react';

import { useGalleryData } from 'Components/Gallery';
import type { GalleryEvents } from 'Components/Gallery/MainGallery';
import { MainGallery } from 'Components/Gallery/MainGallery';
import type { GalleryImageWithTitle } from 'Components/Gallery/MainGallery/MainGallery';

type GalleryTriggerArgs = {
	id: number;
};

type GalleryTrigger = (data: GalleryTriggerArgs) => void;

interface GalleryProviderProps extends PropsWithChildren {
	events?: GalleryEvents;
	overlayBadge?: ReactNode;
}

export const GalleryTriggerContext = createContext<Nullable<GalleryTrigger>>(null);

export const GalleryProvider: React.FC<GalleryProviderProps> = ({ children, events, overlayBadge }) => {
	const [activeIndex, setActiveIndex] = useState<number | null>(null);
	const { images } = useGalleryData();

	const onTrigger = useCallback(
		({ id }: GalleryTriggerArgs) => {
			const imageIdx = images.findIndex((it) => it.id === id);
			const imageIndexFound = imageIdx !== -1;

			setActiveIndex(imageIndexFound ? imageIdx : null);
		},
		[images],
	);

	const onClose = (image: GalleryImageWithTitle) => {
		events?.onClose?.(image);
		setActiveIndex(null);
	};

	return (
		<GalleryTriggerContext.Provider value={onTrigger}>
			{children}
			<MainGallery
				active={activeIndex}
				events={{
					...events,
					onClose,
				}}
				images={images}
				overlayBadge={overlayBadge}
			/>
		</GalleryTriggerContext.Provider>
	);
};

type UseGalleryTrigger = () => GalleryTrigger;

export const useGalleryTrigger: UseGalleryTrigger = () => {
	const trigger = useContext(GalleryTriggerContext);
	if (!trigger) {
		throw new Error('useGalleryTrigger must be used inside GalleryProvider');
	}
	return trigger;
};
